<template>
  <header
    class="app-header"
    :class="{ hover: megamenuOpen || loginOpen }"
    data-back-to-top
  >
    <nav>
      <ul>
        <li class="home-link">
          <pu-homepage-link
            data-test="menu-homepage"
            exclude-type="homepageLink"
            :aria-label="$t('Home')"
            @click="$emit('home-btn-click')"
          >
            <img
              src="@/assets/images/pitchup-logo.png"
              alt=""
              width="80"
              height="70"
              class="logo"
            />
          </pu-homepage-link>
        </li>
        <li
          class="dropdown"
          :class="{ open: megamenuOpen }"
          tabindex="0"
          @click="megamenuClicked()"
          @mouseover="dropDownMenuHandler('megamenu', true)"
          @mouseleave="dropDownMenuHandler('megamenu', false)"
        >
          <span class="nav-item">
            <translate>Explore</translate>
            <img
              src="@/assets/images/down-rounded.svg"
              alt=""
              width="18"
              height="10"
              class="menu-caret"
            />
          </span>
          <mega-menu class="dropdown-content" />
        </li>
        <li>
          <translate
            tag="a"
            :href="`https://www.pitchup.com/${
              $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
            }about/`"
            target="_blank"
          >
            About us
          </translate>
        </li>
        <li class="user-favourites-link">
          <app-header-favourites />
        </li>
        <li
          class="dropdown user-account-link"
          :class="{ open: loginOpen, 'logged-in': isLoggedIn }"
          tabindex="0"
          @mouseover="dropDownMenuHandler('loginmenu', true)"
          @mouseleave="dropDownMenuHandler('loginmenu', false)"
        >
          <template v-if="isLoggedOut || authHasErrors">
            <span class="nav-item">
              <img
                src="@/assets/images/account-icon.svg"
                alt=""
                width="20"
                height="21"
                class="account-icon"
              />
              <translate> Log in </translate>
              <img
                src="@/assets/images/down-rounded.svg"
                alt=""
                width="18"
                height="10"
                class="menu-caret"
              />
            </span>
            <div class="dropdown-content account-menu">
              <nav @click="dropDownMenuHandler('loginmenu', false)">
                <nuxt-link data-allow-mismatch :to="loginUrl">
                  <translate>Log in</translate>
                </nuxt-link>
                <nuxt-link :to="safeLocalePath({ path: '/accounts/signup/' })">
                  <translate>Sign up</translate>
                </nuxt-link>
                <hr />
                <translate tag="a" :href="safeLocalePath({ path: '/join/' })">
                  Add a site
                </translate>
              </nav>
            </div>
          </template>
          <template v-else>
            <nuxt-link :to="safeLocalePath({ name: 'account-dashboard' })">
              <PendingReviewsBadge class="messages-count" />
              <img
                src="@/assets/images/account-icon.svg"
                alt=""
                width="20"
                height="21"
                class="account-icon"
              />
              <translate> User account </translate>
              <img
                src="@/assets/images/down-rounded.svg"
                alt=""
                width="18"
                height="10"
                class="menu-caret"
              />
            </nuxt-link>
            <div
              class="dropdown-content account-menu"
              :class="{ manager: isManager }"
            >
              <nav>
                <template v-if="isManager">
                  <a
                    :href="`/${
                      $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
                    }supplier2/`"
                    target="_blank"
                  >
                    <translate>Manager portal</translate>
                  </a>
                  <hr />
                </template>

                <nuxt-link :to="safeLocalePath({ name: 'account-dashboard' })">
                  <translate v-if="isManager">Customer portal</translate>
                  <translate v-else>Dashboard</translate>
                </nuxt-link>

                <div class="customer-portal">
                  <nuxt-link :to="safeLocalePath({ name: 'account-bookings' })">
                    <translate>Bookings</translate>
                  </nuxt-link>
                  <nuxt-link :to="safeLocalePath({ name: 'favourites' })">
                    <translate>Favourites</translate>
                  </nuxt-link>
                  <nuxt-link :to="safeLocalePath({ name: 'account-reviews' })">
                    <translate>Reviews</translate>
                    <PendingReviewsBadge />
                  </nuxt-link>
                  <nuxt-link
                    :to="
                      safeLocalePath({ name: 'account-availability-alerts' })
                    "
                  >
                    <translate>Availability alerts</translate>
                  </nuxt-link>
                  <nuxt-link :to="safeLocalePath({ name: 'account' })">
                    <translate>Account details</translate>
                  </nuxt-link>
                  <nuxt-link :to="safeLocalePath({ name: 'account-settings' })">
                    <translate>Settings</translate>
                  </nuxt-link>
                </div>
                <hr />
                <a @click.prevent="onLogOutClick">
                  <translate> Log out </translate>
                </a>
              </nav>
            </div>
          </template>
        </li>
      </ul>
    </nav>
  </header>
</template>

<script lang="ts">
import AuthMixin from '~/mixins/AuthMixin'
import { Component, Vue, toNative } from '~/utility/pu-class-decorator'
import AppMenuMixin from '../../apps/app-menu/mixin'

@Component({
  mixins: [AppMenuMixin, AuthMixin],
})
class AppHeaderDesktop extends Vue {
  megamenuOpen = false
  loginOpen = false
  menuTimer: ReturnType<typeof setTimeout>

  created() {
    // Shim $puLocalePath if not available to prevent render errors
    if (!this.$puLocalePath) {
      // @ts-ignore - Adding shim dynamically
      this.$puLocalePath = this.safeLocalePath.bind(this)
    }
  }

  // Safe wrapper for $puLocalePath that falls back to basic path if plugin not ready
  safeLocalePath(route: any): string {
    try {
      // Check if the real plugin is available (not our shim)
      const nuxtApp = (this as any).$nuxt || (this as any).nuxtApp
      const realPlugin = nuxtApp?.$puLocalePath
      if (realPlugin && realPlugin !== this.safeLocalePath) {
        return realPlugin(route)
      }
      // Fallback: construct basic path
      if (route.path) {
        return route.path
      }
      if (route.name) {
        // Basic route name to path mapping for critical routes
        const routeMap: Record<string, string> = {
          'account-dashboard': '/accounts/my-account/',
          'account-bookings': '/accounts/bookings/',
          'account-messages': '/accounts/messages/',
          'account-settings': '/accounts/settings/',
          account: '/accounts/',
          favourites: '/accounts/favourites/',
          'account-reviews': '/accounts/reviews/',
          'account-availability-alerts': '/accounts/availability-alerts/',
        }
        return routeMap[route.name] || '/'
      }
      return '/'
    } catch (error) {
      console.error('Error in safeLocalePath:', error)
      return '/'
    }
  }

  get loginUrl() {
    const next = ['index', 'accounts-login'].includes(this.$route.name || '')
      ? undefined
      : /**
         * url fragment is not available during SSR, so rendering a link with it
         * creates hydartion mismatch errors
         * https://stackoverflow.com/questions/60165884/vue-ssr-lose-url-hash-when-redirect
         */
        this.$route.fullPath.split('#')[0]
    return this.safeLocalePath({ path: '/accounts/login/', query: { next } })
  }

  dropDownMenuHandler(menu: string, open: boolean) {
    clearTimeout(this.menuTimer)
    this.menuTimer = setTimeout(async () => {
      await this.$nextTick()
      if (menu === 'megamenu') {
        this.megamenuOpen = open
        this.loginOpen = false
      }
      if (menu === 'loginmenu') {
        this.loginOpen = open
        this.megamenuOpen = false
      }
    }, 150)
  }

  megamenuClicked() {
    this.megamenuOpen = !this.megamenuOpen
  }

  onLogOutClick() {
    if (this.$route?.meta?.middleware?.includes('auth-redirect')) {
      this.logOut('/')
    } else {
      this.logOutInPlace()
    }
    this.dropDownMenuHandler('loginmenu', false)
  }
}

export default toNative(AppHeaderDesktop)
</script>

<style scoped>
header {
  display: block;
  width: 100%;
  position: relative;
  max-width: var(--page-width-max);
}

header:hover,
header.hover {
  z-index: 12;
}

nav > ul {
  display: flex;
  min-height: 5.5em;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 var(--page-width-padding);
}

nav > ul > li {
  cursor: pointer;
  flex-shrink: 0;
}

nav > ul > li:first-child {
  padding-right: 1em;
}

@media (min-width: 768px) {
  nav > ul > li:first-child {
    padding-right: 4em;
  }
}

nav > ul > li > a,
nav > ul > li > .nav-item {
  align-items: center;
  border-radius: 2em;
  border: 3px solid transparent;
  color: #6a5f5c;
  display: flex;
  font-size: 1.25em;
  font-weight: bold;
  padding: 0.35em 0.75em;
  position: relative;
  text-decoration: none;
  transition: all 0.2s ease-out;
}

nav > ul > li:hover > a,
nav > ul > li > a:focus-visible,
nav > ul > li.dropdown:focus-visible .nav-item,
nav > ul > li.open > a,
nav > ul > li.open > .nav-item,
nav > ul > li.selected > a {
  color: var(--colour-dark-blue);
  border-color: var(--colour-orange);
  z-index: 2;
  background-color: white;
  outline: none;
}

nav > ul > li.dropdown:focus-visible {
  outline: none;
}

nav > ul > li.home-link a {
  padding: 0.35em 0;
}

nav > ul > li.home-link:hover a,
nav > ul > li.home-link a:focus-visible {
  border-color: transparent;
  transform: scale(1.1);
  transition-duration: 0.15s;
  background-color: transparent;
  outline: none;
}

.user-account-link {
  position: relative;
}
.user-account-link .messages-count {
  position: absolute;
  bottom: 0.6em;
  left: 0.2em;
}
.dropdown-content :deep(.notification-badge) {
  display: inline-flex;
  font-size: var(--body);
}

.dropdown-content {
  display: none;
  top: auto;
  position: absolute;
  min-width: 9em;
  cursor: initial;
}

.logged-in .dropdown-content.account-menu {
  min-width: 13em;
}

.account-menu.manager .customer-portal a {
  padding-left: 2em;
}

.dropdown {
  position: static;
}

.dropdown.open {
  animation-name: fadein;
  animation-duration: 250ms;
}

.dropdown.open .dropdown-content {
  display: block;
  pointer-events: auto;
  z-index: 2;
}

.dropdown-content :deep(nav) {
  background: white;
  border-radius: 4px;
  box-shadow: 0 1em 1em rgba(0, 0, 0, 0.4);
  margin: 1em auto 0 auto;
  max-width: var(--page-width-max);
  overflow: hidden;
}

.account-menu nav a {
  align-items: center;
  color: var(--text-default);
  column-gap: 0.25em;
  display: flex;
  font-size: 1em;
  justify-content: start;
  line-height: 1.75;
  padding: 0.5em 1em;
  text-decoration: none;
}

.account-menu nav a.router-link-exact-active {
  background-color: var(--colour-page-background);
}

.account-menu nav a:hover {
  background-color: var(--colour-dark-blue);
  color: white;
}

.dropdown-content :deep(.accommodation-types) {
  flex-direction: column;
}

.dropdown-content :deep(.accommodation-types legend) {
  font-size: inherit;
  padding: 0 0 0.5em 0;
  margin-left: 0;
}

/* pagemask */
.dropdown.open::after {
  background: rgba(0, 0, 0, 0.5);
  bottom: 0;
  content: '';
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1;
  pointer-events: none;
}

@keyframes fadein {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.dropdown.open > a {
  position: relative;
}

.dropdown.open > a::after,
.dropdown.open > .nav-item::after {
  background: white;
  border-radius: 0.35em 0 0 0;
  bottom: -2.5em;
  content: '';
  display: block;
  height: 2em;
  left: calc(50% - 1em);
  position: absolute;
  transform: rotate(45deg);
  width: 2em;
  z-index: 2;
  box-shadow: 0 0.5em 1em 0 rgba(0, 0, 0, 0.4);
}

.menu-caret {
  display: block;
  flex-shrink: 0;
  align-self: center;
  margin: 0.2em 0 0 0.5em;
  width: 0.9em;
  opacity: 0.5;
  transition: transform 200ms ease-in-out;
}

.dropdown.open .menu-caret {
  transform: scaleY(-100%);
  margin: 0 0 0 0.5em;
}

nav :deep(.favourites-link) {
  position: relative;
  isolation: isolate;
}

nav :deep(.favourites-link .sr-only) {
  display: flex;
  position: relative;
  left: auto;
  overflow: visible;
  width: auto;
  height: auto;
}

nav :deep(.favourites-link svg),
.account-icon {
  width: 1.25em;
  height: auto;
  margin: 0.1em 0.1em 0 0;
}

nav :deep(.favourites-link .circle) {
  left: auto;
  top: 0.75em;
  right: calc(100% - 1.2em);
}

nav :deep(.favourites-link .circle.with-count) {
  right: calc(100% - 1.5em);
}

nav :deep(.favourites-link:has(.with-count)) {
  padding-left: 1.1em;
}

nav :deep(a.favourites-link.router-link-active) {
  color: var(--colour-dark-blue);
  border-color: var(--colour-orange);
}

.account-icon {
  width: 1em;
  margin-right: 0.4em;
}

.active-tab-favourites
  .user-account-layout.desktop
  .app-header
  :deep(li.user-account-link > a) {
  color: #6a5f5c;
  border-color: transparent;
}
</style>
